import React from "react";
import { Box, TablePagination, CircularProgress, Button, Typography } from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import BondTable from "../BondTable";
import { tokens } from "../../Common/designTokens";

const noop = () => { };

const FixLogsTable = ({
  rows,
  columns,
  loading,
  page,
  rowsPerPage,
  totalElements,
  onPageChange,
  onRowsPerPageChange,
  sortConfig,
  onSort,
  sectionKey,
  onDecodeClick,
  decodeHighlightedRow,
  decodeHighlightActive = false,
  showNoLogs = false,
}) => {
  const isInitialLoading = loading && (!rows || rows.length === 0);

  // Custom render for the decode action column
  const renderDecodeButton = (row) => (
    <Button
      size="small"
      variant="outlined"
      startIcon={<CodeIcon fontSize="small" />}
      onClick={(e) => {
        e.stopPropagation();
        onDecodeClick?.(row);
      }}
      sx={{
        textTransform: "none",
        fontSize: "11px",
        py: 0.25,
        px: 1,
        minWidth: "auto",
        borderRadius: "4px",
        borderColor: tokens.palette.primary,
        color: tokens.palette.primary,
        "&:hover": {
          backgroundColor: tokens.colors.defaultGridHoverColor,
          borderColor: tokens.palette.primary,
        },
      }}
    >
      Decode
    </Button>
  );

  // Enhance columns with custom render for decode action
  const enhancedColumns = columns.map((col) => {
    if (col.key === "_decode") {
      return {
        ...col,
        render: (value, row) => renderDecodeButton(row),
      };
    }
    return col;
  });

  return (
    <>
      <Box
        sx={{
          padding: "0px",
          height: "calc(100vh - 260px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Underlying table (always rendered) */}
        <BondTable
          source="quickfix"
          sectionKey={sectionKey}
          selectedColumns={enhancedColumns}
          filteredData={rows}
          handleRowClick={noop}
          handleViewJson={noop}
          handleBuyClick={noop}
          handleCusipClick={noop}
          onSort={onSort}
          sortConfig={sortConfig}
          orderData={null}
          setOrderData={noop}
          decodeHighlightedRow={decodeHighlightedRow}
          decodeHighlightActive={decodeHighlightActive}
        />

        {/* Initial load: center spinner on blank-ish area */}
        {isInitialLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              zIndex: 2,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Overlay loader when data already exists: keep data visible in background */}
        {!isInitialLoading && loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.4)", // semi-transparent
              zIndex: 2,
              pointerEvents: "none", // let user still scroll if you like; change to "auto" to block
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {showNoLogs && !loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: tokens.colors.fontColour85,
              }}
            >
              No Logs Available
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          color: "white",
          zIndex: 1000,
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }}
      >
        <TablePagination
          component="footer"
          sx={{
            ".MuiSelect-select": { p: "4px 24px 5px 8px !important" },
          }}
          showFirstButton
          showLastButton
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          page={page}
          onRowsPerPageChange={onRowsPerPageChange}
          onPageChange={onPageChange}
          count={totalElements}
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`}
        />
      </Box>
    </>
  );
};

export default FixLogsTable;
